import { cloneDeep } from "lodash";
import { HttpClient, HttpEvent, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, finalize, map, Observable, of, switchMap, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { IPayload, IQuestion, Payload } from "../interface/filters";
import {
  genInterviewSummaryTableConfig
} from "../modules/pages/interview-summary/interview-summary/interview-summary-table-config";
import { InterviewAdapter } from "../modules/pages/interview-summary/models/interview-adapter";
import { InterviewSummary } from "../modules/pages/interview-summary/models/interview-summary";
import { IApiService } from "../modules/records/models/api/i-api-service";
import { IMappedApiCollection } from "../modules/records/models/api/i-mapped-api-collection";
import { TextCol } from "../modules/records/models/columns/text-col";
import { TableStructure } from "../modules/records/models/table-structure";
import { RatingCol } from "../shared/table-col-configs/rating-col";
import { ApplicationService } from "./application.service";
import { MyUtils } from "./my-utils.service";
import { RecoveryPlanService } from "./recovery-plan.service";
import { MeUser } from "./models/me-user";
import { Router } from "@angular/router";
import { Role } from "../enums/role";
import { DownloadLogService } from "./download-log.service";
import { TagOnlineRepDialogComponent } from "../shared/dialogs/tag-online-rep-dialog/tag-online-rep-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { OnlineRepCommentDialogComponent } from "../shared/dialogs/online-rep-comment-dialog/online-rep-comment-dialog.component";
import { DataMiningService } from "./data-mining.service";
import { LoaderService } from "./loader.service";
import { delay } from "rxjs/operators";
import { splitInterviewSummaryQuestions } from "../modules/pages/interview-summary/interview-summary/interview-summary-table-columns/interview-summary-question-order";
export interface InterviewSummaryResponse {
  errors: any
  questionModel: QuestionModel[]
  data: Survey[]
  paginationInfo: any
}

export interface QuestionModel {
  questionCode: string
  questionType: string
  questionOrder: number
  questionLabel: string
  questionLabelLang: string
  question: string
  questionLang: string
  answers: Answer[]
}

export interface Answer {
  questionCode: string
  answerValue: string
  answerTranslation: string | null
  answerTranslationLang: string | null
}

export interface Survey {
  surveyRequestID: string
  dataSurvey: string
  rpStatus: string
  completedDate: string
  brand: string
  brandName: string
  region: string
  market: string
  dealer: string
  outlet: string
  rpDtStatus: string
  rpCreateBtn: string
  vin: string
  customerName: string
  dealerName: string
  channel: string
  vehicle: string
  model: string
  engine: string
  dealerGroup: string
  importer: string
  distributorName: string
  caseNumber: string
  hotAlert: string
  lob: string
  caseReason: string
  caseOrigin: string
  categoryLevel1: string
  categoryLevel2: string
  categoryLevel3: string
  categoryLevel4: string
  categoryLevel5: string
  technicalIssue1: string
  technicalIssue2: string
  technicalIssue3: string
  callReason: string
  caseOwner: string
  caseDateTimeOpened: string
  caseDateTimeClosed: string
  invitationDate: string
  haScore: string
  haTrigger: string
  mined: string
  contactReasonL1: string
  contactReasonL2: string
  dissatisfactionReason1Level1: string
  dissatisfactionReason1Level2: string
  dissatisfactionReason2Level1: string
  dissatisfactionReason2Level2: string
  personEnteringInfo: string
  rpDescription: string
  rpStartDate: any
  agentName: string
  anonimityRelease: string
  answers: Answer[]
}
@Injectable({
  providedIn: "root",
})
export class InterviewsV2Service implements IApiService<InterviewSummary> {
  lastSort: string | null = null;
  lastPageIndexQuery: number = 0;
  isShopper: boolean = false;
  endpoint = environment.csibefrontend;
  isLoading = false;
  isInterviewSync = true
  totalRecords: number = 0;
  public tableDatasSubject: BehaviorSubject<any>;
  public tableDatas: Observable<any>;
  private _interviews = new BehaviorSubject<InterviewSummary[]>([]);

  private idsTableSubject: BehaviorSubject<any>;
  public idsTable: Observable<string[]>;

  public interviewSummaryPath?: string;
  public exportInterviewSummaryPath?: string;
  public asyncExportInterviewSummaryPath?: string;
  public exportDeletedInterviewsSummaryPath?: string;
  public asyncExportDeletedInterviewsSummaryPath?: string;


  public currentQuestionModel = new BehaviorSubject<QuestionModel[]>([]);

  user: MeUser = {
    agent: null,
    csiLevel: Role.ROLE_DEALER,
    brand: "",
    csiProfile: Role.ROLE_DEALER,
    dealerInfo: [],
    dealer: "",
    dealerGroup: [],
    dealerName: "",
    defaultLocale: "",
    distributor: null,
    elementBrandColor: "",
    email: "",
    firstBrandColor: "",
    market: "",
    menu: [],
    outlet: "",
    pathbrand: "",
    profileCodeMenu: "",
    region: "",
    roles: [],
    scope: "",
    scopeEntities: { network: "", vehicle: "" },
    secondBrandColor: "",
    subRegion: ""
  };
  question: IQuestion = {
    questionCode: "",
    section: "",
    tab: "",
    orderByScore: "",
    locale: "",
    limit: ""
  }

  constructor(
    private interviewAdapter: InterviewAdapter,
    private http: HttpClient,
    private utils: MyUtils,
    private recoveryPlanService: RecoveryPlanService,
    private applicationService: ApplicationService,
    private dataMiningService: DataMiningService,
    private ds: DownloadLogService,
    public router: Router,
    private dialog: MatDialog,
    private loader: LoaderService
  ) {

    this.tableDatasSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("tableDatas")!)
    );
    this.tableDatas = this.tableDatasSubject.asObservable();

    this.idsTableSubject = new BehaviorSubject<any>([]);
    this.idsTable = this.idsTableSubject.asObservable();

    this.setInterviewSummaryPath();
  }

  upload(file: File, additionalData: Map<string, string> | undefined): Observable<HttpEvent<any>> {
    const formData: FormData = new FormData();

    formData.append('file', file);

    if (additionalData) {
      for (let [key, value] of additionalData.entries()) {
        if (value != null)
          formData.append(key, value);
      }
    }
    const req = new HttpRequest('POST', `${this.endpoint}interviews/v1/recall-campaign/upload`, formData, {
      reportProgress: true,
      responseType: 'json'
    });

    return this.http.request(req);
  }

  setInterviewSummaryPath(): void {
    this.interviewSummaryPath = "v5/interviewSummary";
    this.exportInterviewSummaryPath = "v4/exportInterviewsSummary"
    this.asyncExportInterviewSummaryPath = "v5/exportInterviewsSummary"
    this.exportDeletedInterviewsSummaryPath = "v5/exportDeletedInterviewsSummary"
    this.asyncExportDeletedInterviewsSummaryPath = "roar-reports/v1/interviews-summary-deleted/async"
    
    if (this.isCec) {
      this.exportInterviewSummaryPath = "v3/exportInterviewsSummary"
      this.asyncExportInterviewSummaryPath = "v6/exportInterviewsSummary"
    }

  }
  get isCec() {
    return ['CECRFI', 'CECCLAIM'].includes(this.applicationService.navBarSelectValue.idItem.toUpperCase())
  }
  get item$() {
    return this._interviews.asObservable();
  }
  public setIdsTableValue(idsTable: any) {
    localStorage.setItem("idsTable", JSON.stringify(idsTable));
    this.idsTableSubject.next(<any>idsTable);
  }
  public get idTableValue() {
    return this.idsTableSubject.value;
  }

  public setTableDatasValue(idsTable: any) {
    localStorage.setItem("tableDatas", JSON.stringify(idsTable));
    this.tableDatasSubject.next(<any>idsTable);
  }

  public get tableDatasValue() {
    return this.tableDatasSubject.value;
  }

  getAll(
    filter: IPayload,
    skip: number,
    limit: number,
    sortDirection: string,
    sortBy: string,
    extraFilters: {
      NPS: string;
      NSAT: string;
      channel: string;
      searchBy: string;
      searchFor: string;
      recovery: string;
    },
    tableConfig: TableStructure<InterviewSummary>,
    getOnlyData?: boolean
  ) {
    this.totalRecords = 0;

    let payload = this.buildFilters(filter, skip, limit, sortDirection, sortBy, extraFilters);
    this.isLoading = true;
    return this.http.post<any>(this.endpoint + this.interviewSummaryPath, payload).pipe(
      map((res) => {
        return res.body
      }),
      map((res) => {

        if (res) {
          if (res.paginationInfo) {
            this.lastSort = res.paginationInfo.lastSort
            this.currentQuestionModel.next(res.questionModel);
          } else {
            res.data = [];
            res.paginationInfo = {
              itemsPerPage: payload.extraFilters.ItemsPerPage,
              page: payload.extraFilters.Page,
              totalElements: 0,
              totalPages: 0
            };
          }
        }
        return this.interviewAdapter.mapCollection(this.mapAnswers(res, tableConfig));
      })
      ,
      tap((res) => {
        this.totalRecords = res.count;

        if (!getOnlyData) {
          this.setTableDatasValue(res.items);
        }

        this._interviews.next(res.items)
      }),
      tap((_) => (this.isLoading = false))
    )
  }

  mapAnswers(res: InterviewSummaryResponse | null, tableConfig: TableStructure<InterviewSummary>) {

    let result: any = { data: [], paginationInfo: [] }
    if (res) {
      this.removeQuestionColsOnReloadData(tableConfig);
      const createQuestionColumn = (question: QuestionModel): TextCol<any> | RatingCol<any> => {
        const column = question.questionType === "TenStarNPS" || question.questionType === "TenStar" || question.questionType === "TenStarAvg"
          ? new RatingCol<any>(question.questionCode, question.questionLabel)
          : new TextCol<any>(question.questionCode, false, question.questionLabel);

        column.sortable = this.router.url.endsWith('interview-summary-elastic');
        if (question.questionType === "FreeTextLarge") {
          column.sortable = false;
        }
        column.removable = false;
        column.isQuestionCol = true;
        return column;
      };

      const hasPlatformLayout = tableConfig.cols.some((column) => column.columnDef === "anonimityRelease");
      if (hasPlatformLayout) {
        const questionGroups = splitInterviewSummaryQuestions(this.currentQuestionModel.value);
        this.insertQuestionColumns(tableConfig, "anonimityRelease", questionGroups.priority.map(createQuestionColumn));
        this.insertQuestionColumns(
          tableConfig,
          tableConfig.questionColumnsAnchor || "rpDtStatus",
          questionGroups.remaining.map(createQuestionColumn)
        );
      } else {
        this.insertQuestionColumns(tableConfig, "rpDtStatus", this.currentQuestionModel.value.map(createQuestionColumn));
      }

      result.data = res.data.map(survey => {
        let answersObject: Record<string, string> = {};
        this.currentQuestionModel.value.forEach(el => {
          let answer = survey.answers.find(item => item.questionCode === el.questionCode);
          answersObject[el.questionCode as string] = answer?.answerTranslation ?? answer?.answerValue ?? "";

        })
        return new InterviewSummary({
          ...survey,
          ...answersObject,
          surveyRequestId: survey.surveyRequestID
        })
      })
      result.paginationInfo = res.paginationInfo;
    }
    return result;
  }

  removeQuestionColsOnReloadData(tableConfig: TableStructure<InterviewSummary>) {
    tableConfig.cols = tableConfig.cols.filter(el => !el.isQuestionCol);
    return tableConfig
  }

  private insertQuestionColumns(
    tableConfig: TableStructure<InterviewSummary>,
    anchorColumnDef: string,
    columns: (TextCol<any> | RatingCol<any>)[]
  ) {
    if (!columns.length) {
      return;
    }

    const anchorIndex = tableConfig.cols.findIndex((column) => column.columnDef === anchorColumnDef);
    const insertIndex = anchorIndex >= 0 ? anchorIndex : Math.max(0, tableConfig.cols.length - 2);
    tableConfig.cols.splice(insertIndex, 0, ...columns);
  }

  changeStatus(el: InterviewSummary, status: "approve" | "reject") {
    return of(null);
  }

  showDialogCommentsOnlineRep(el: any) {
    return this.dialog
      .open(OnlineRepCommentDialogComponent, {
        maxHeight: "120vh",
        maxWidth: "100vh",
        data: {
          reviewId: el.reviewId,
          dealerId: el.dealerId,
          marketId: el.marketId,
          regionId: el.regionId,
          brandId: el.brandId,
          section: el.section,
          comments: el.comments,
          response: el.response,
          customerName: el.customerName
        }
      })
      .afterClosed()
      .pipe(switchMap((formData) => (formData ? this.createCommentOnlineRep(formData) : of(null))))
  }

  private createCommentOnlineRep(formValue: any) {
    return this.http.post(this.endpoint + `v1/reviewReply`, formValue);
  }

  showDialogTagOnlineRep(el: any) {
    return this.dialog
      .open(TagOnlineRepDialogComponent, {
        height: "auto",
        width: "450px",
        data: {
          businessId: el.businessId,
          reviewId: el.reviewId,
          section: el.section,
        }
      })
      .afterClosed()
      .pipe(switchMap((formData) => (formData ? this.createTagOnlineRep(formData) : of(null))))

  }

  private createTagOnlineRep(formValue: any): Observable<any> {
    this.loader.startLoader()
    return this.http.post(this.endpoint + `v1/setOnlineReputationReviewTag`, formValue).pipe(
      map((res: any) => res),
      finalize(() => this.loader.stopLoader()))

  }

  get(filter: Payload) {
    return this.http
      .post<any>(
        this.endpoint + this.interviewSummaryPath,
        filter
      )
      .pipe(
        map((res) => res),
        catchError((err) => {
          return of({} as any);
        })
      );
  }

  createRecoveryPlan(el: InterviewSummary) {
    return this.recoveryPlanService.createPlan({
      brand: el.brand,
      completedDate: el.completedDate,
      isEdit: false,
      outletId: el.outlet || "",
      surveyId: el.surveyRequestId,
      title: el.title,
      description: el.description,
      dateEnd: el.dateEnd,
    }).pipe(
      // switchMap((r) => {
      //
      //   let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
      //   payload.question.section = this.applicationService.navBarSelectValue.idItem;
      //   payload.question.locale = this.applicationService.languageSelectValue;
      //   return this.get(payload);
      // }),
      // tap(),
      delay(1000),
      map(res => (!res ? null : { success: (res as any).result === "OK" })),
      catchError((err) => {


        return of(({ success: false }));
      })
    );
  }

  createDataMining(el: InterviewSummary) {
    return this.dataMiningService.createPlan({
      brand: el.brand,
      completedDate: el.completedDate,
      isEdit: false,
      outletId: el.outlet || "",
      surveyId: el.surveyRequestId,
      title: el.title,
      description: el.description,
      dateEnd: el.dateEnd,
      contactReasonL1: "",
      contactReasonL2: "",
      dissatisfacionReason1Level1: "",
      dissatisfacionReason1Level2: "",
    }).pipe(
      switchMap((r) => {

        let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
        payload.question.section = this.applicationService.navBarSelectValue.idItem;
        payload.question.locale = this.applicationService.languageSelectValue;
        return this.get(payload);
      }),
      tap(),
      catchError((err) => {
        return of([]);
      })
    );
  }

  downloadExcel(el: InterviewSummary, isElastic: boolean, extraFilters: any) {
    const downloadExportInterviews = this.isInterviewSync ? this.asyncExportInterviewSummaryPath : this.exportInterviewSummaryPath;

    const payload = this.buildFilters(this.utils.settingsPayloadValue.payload, 0, 0, 'ASC', 'surveyRequestId', extraFilters);

    payload.question.section = this.applicationService.navBarSelectValue.idItem;
    payload.question.tab = "REPORT";
    payload.question.locale = this.applicationService.languageSelectValue;

    return this.http
      .post(this.endpoint + downloadExportInterviews, payload, {
        responseType: "blob",
      })
      .pipe(
        tap((data) => {
          if (!this.isInterviewSync) {
            const file = new Blob([data], { type: "text/csv;charset=utf-8" });
            const fileURL = URL.createObjectURL(file);
            const a = document.createElement("a");
            a.href = fileURL;
            a.download = "exportInterviewsSummary" + ".xlsx";
            a.target = "_blank";
            a.click();
          } else {
            this.ds.openSnackBar();
          }

        })
      );
  }

  downloadDeleted(el: InterviewSummary, isElastic: boolean, extraFilters: any) {
    const payload = this.buildFilters(this.utils.settingsPayloadValue.payload, 0, 0, 'ASC', 'surveyRequestId', extraFilters);

    payload.question.section = this.applicationService.navBarSelectValue.idItem;
    payload.question.tab = "REPORT";
    payload.question.locale = this.applicationService.languageSelectValue;

    return this.http
      .post(this.endpoint + this.asyncExportDeletedInterviewsSummaryPath, payload, {
        responseType: "blob",
      })
      .pipe(
        tap((data) => {
          this.ds.openSnackBar();
        })
      );
  }

  print(el: InterviewSummary) {
    return of("print");
  }


  private buildFilters(
    filter: any,
    skip: number,
    limit?: number,
    sortDirection?: string,
    sortBy?: string,
    extraFilters?: {
      NPS: string;
      NSAT: string;
      recovery: string;
      channel: string;
      searchBy: string;
      searchFor: string;
    }
  ) {
    let payload: IPayload = Object.assign({}, filter || {});
    let isNextPage = skip > this.lastPageIndexQuery;

    payload.question = {
      limit: "",
      locale: this.applicationService.languageSelectValue,
      orderByScore: "",
      questionCode: "",
      section: this.applicationService.navBarSelectValue.idItem,
      tab: 'INTERVIEWS-SUMMARY',
    };
    payload.extraFilters = {
      OrderByColumn: (sortBy?.startsWith('stellantis-') ? '$$' + sortBy : sortBy) ?? "",
      Page: skip ? skip + 1 : 1,
      ItemsPerPage: limit,
      OrderBy: sortDirection || "",
      SearchColumn: extraFilters && extraFilters.searchBy ? extraFilters.searchBy : "",
      SearchFor: extraFilters && extraFilters.searchFor ? extraFilters.searchFor : "",
      Nps: extraFilters?.NPS || extraFilters?.NSAT || "",
      RpCreate: extraFilters?.recovery && extraFilters?.recovery === "Recovery" ? 1 : 0 || "",
      Channel: extraFilters?.channel || "",
      lastSort: isNextPage ? this.lastSort : null
    };

    return payload;
  }

  genTableConfig(user: MeUser, question: IQuestion) {
    this.user = user;
    this.question = question;
    const isSolOrDelivery = this.applicationService.navBarSelectValue.idItem === 'ORDER' || this.applicationService.navBarSelectValue.idItem === 'DELIVERY'
    const withDateSort = !this.router.url.endsWith('/interview-summary');
    let tableConfig = genInterviewSummaryTableConfig(this, user, question, isSolOrDelivery, this.ds, this.utils.settingsPayloadValue.payload, withDateSort, this.applicationService.navBarSelectValue.idItem);
    return tableConfig;
  }

  elementWasVisited(element: InterviewSummary) {
    let wasVisited = of(false);
    if (this.idTableValue) {
      wasVisited = this.idsTable.pipe(map((ids) => ids.includes(element.surveyRequestId)));
    }
    return wasVisited;
  }

  getResponsesTemplate() {
    return this.http.get(this.endpoint + `v1/getOnlineRepDealer`);
  }

  saveResponse(formData: any) {
    return this.http.post(this.endpoint + `v1/createOnlineRepResponse`, formData);
  }

  deleteResponse(idTemplate: string) {
    return this.http.get(this.endpoint + `v1/deleteOnlineRepDealerResponse?idTemplate=${idTemplate}`);
  }

}

